import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { Travelsheet } from 'api/report/types';
import { TravelsheetKeyType } from 'report/logic/timesheet/calculations';
import TextField from 'components/interface/textField/TextField';
import Card from 'components/interface/card/Card';
import Collapse from '@mui/material/Collapse';
import { Button } from 'components/Button';
import { DatePicker } from 'components/interface/datePicker/DatePicker';

const useStyles = makeStyles()(theme => ({
    cardWrapper: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    fieldWrapper: {
        'width': '100%',
        'display': 'flex',
        'flexDirection': 'column',
        'justifyContent': 'flex-start',
        '& > *': {
            marginTop: theme.spacing(1),
        },
    },
}));

interface Props {
    travelsheet: Travelsheet | null;
    canSubmit: boolean;
    disabled: boolean;
    onTravelsheetFieldChange: (
        key: TravelsheetKeyType,
        index: number
    ) => (value: string) => void;
}

const TravelsheetComponent = ({
    travelsheet,
    canSubmit,
    disabled,
    onTravelsheetFieldChange,
}: Props): JSX.Element => {
    const { classes } = useStyles();
    const [open, setOpen] = React.useState<{
        [key in TravelsheetKeyType]: boolean;
    }>({
        travel1: true,
        travel2: true,
        travel3: true,
        travel4: true,
        travel5: true,
        travel6: true,
        travel7: true,
    });

    const handleToggleHidden = (key: TravelsheetKeyType) =>
        setOpen(prev => ({ ...prev, [key]: !prev[key] }));

    const buildCard = (key: TravelsheetKeyType) => {
        if (!canSubmit && travelsheet && travelsheet[key] === null) {
            return null;
        }
        const values = travelsheet?.[key] ?? [];
        return (
            <Card className={classes.cardWrapper}>
                <DatePicker
                    label='Datum'
                    value={values[0] || ''}
                    onChange={onTravelsheetFieldChange(key, 0)}
                    disabled={open[key] || !canSubmit || disabled}
                    fullWidth
                />
                <Collapse timeout={500} in={!open[key]}>
                    <div className={classes.fieldWrapper}>
                        {buildTextField(
                            'Färdsätt',
                            values && values[1] ? values[1] : '',
                            'text',
                            key,
                            1
                        )}
                        {buildTextField(
                            'Från',
                            values && values[2] ? values[2] : '',
                            'text',
                            key,
                            2
                        )}
                        {buildTextField(
                            'Till',
                            values && values[3] ? values[3] : '',
                            'text',
                            key,
                            3
                        )}
                        {buildTextField(
                            'Antal mil',
                            values && values[4] ? values[4] : '',
                            'number',
                            key,
                            4
                        )}
                        {buildTextField(
                            'Utlägg enl kvitto',
                            values && values[5] ? values[5] : '',
                            'number',
                            key,
                            5
                        )}
                    </div>
                </Collapse>
                <Button
                    sx={{ mt: 2 }}
                    disabled={disabled}
                    variant='outlined'
                    onClick={() => handleToggleHidden(key)}>
                    {open[key] ? (canSubmit ? 'Ändra' : 'Visa') : 'Dölj'}
                </Button>
            </Card>
        );
    };

    const buildTextField = (
        label: string,
        value: string,
        type: string,
        key: TravelsheetKeyType,
        index: number
    ) => {
        return (
            <TextField
                id={key + index}
                type={type}
                key={key + '' + index}
                value={value || ''}
                label={label}
                onChange={onTravelsheetFieldChange(key, index)}
                disabled={open[key] || !canSubmit || disabled}
                fullWidth
            />
        );
    };

    return (
        <>
            {buildCard('travel1')}
            {buildCard('travel2')}
            {buildCard('travel3')}
            {buildCard('travel4')}
            {buildCard('travel5')}
            {buildCard('travel6')}
            {buildCard('travel7')}
        </>
    );
};

export default TravelsheetComponent;
